import { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAppSelector } from '@/store/hooks'
import type { UserRole } from '@/types'

interface ProtectedRouteProps {
  children: ReactNode
  redirectTo?: string
  // When provided, the authenticated user's role must be in this list.
  allowedRoles?: UserRole[]
}

export function ProtectedRoute({
  children,
  redirectTo = '/login',
  allowedRoles,
}: ProtectedRouteProps) {
  const { isAuthenticated, user } = useAppSelector(s => s.auth)
  const location = useLocation()

  // AC-2.1.1-022/024: no valid session -> show Login (redirect unauthenticated).
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  // Role-based access guard (Admin/Manager/Operator). The backend returns a
  // `roles` array; accept a match on the normalized singular `role` OR any role
  // in the array.
  if (allowedRoles && user) {
    const userRoles =
      user.roles && user.roles.length ? user.roles : [user.role]
    if (!userRoles.some(r => r && allowedRoles.includes(r))) {
      return <Navigate to='/dashboard' replace />
    }
  }

  return <>{children}</>
}

export default ProtectedRoute
